/**
 * PATH: apps/quote/src/components/builder/application-selector-modal/ApplicationResultItem.tsx
 * PURPOSE: Single application row in the results list
 */

import { Package, Check, Zap, DollarSign, Shield, ChevronRight, Star } from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import type { Application } from '@/lib/quoteApi'

interface ApplicationResultItemProps {
  app: Application
  index: number
  highlightedIndex: number
  selectedId?: string | null
  onSelect: (app: Application) => void
  onHover: (app: Application, index: number) => void
}

export default function ApplicationResultItem({
  app,
  index,
  highlightedIndex,
  selectedId,
  onSelect,
  onHover,
}: ApplicationResultItemProps) {
  return (
    <button
      onClick={() => onSelect(app)}
      onMouseEnter={() => onHover(app, index)}
      className={cn(
        'w-full px-4 py-3 text-left flex items-start gap-4 transition-colors',
        index === highlightedIndex && 'bg-indigo-50 dark:bg-indigo-900/20',
        app.id === selectedId && 'bg-green-50 dark:bg-green-900/20'
      )}
    >
      {/* Icon */}
      <div className={cn(
        'p-2 rounded-lg shrink-0 mt-0.5',
        app.id === selectedId ? 'bg-green-100 dark:bg-green-900/40' : 'bg-slate-100 dark:bg-slate-800'
      )}>
        {app.id === selectedId ? <Check className="w-5 h-5 text-green-600 dark:text-green-400" /> : <Package className="w-5 h-5 text-slate-500" />}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-900 dark:text-white">{app.menu_item || app.name}</span>
          {app.code && (
            <span className="text-xs font-mono text-slate-400 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">{app.code}</span>
          )}
          {(app.usage_count || 0) > 10 && (
            <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
              <Star className="w-3 h-3 fill-amber-500" /> Popular
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
          {app.supplier_name && <span>{app.supplier_name}</span>}
          {app.area_type_display && <span>• {app.area_type_display}</span>}
          {app.substrate_type_display && <span>• {app.substrate_type_display}</span>}
          {app.application_situation && <span className="text-indigo-600 dark:text-indigo-400">• {app.application_situation}</span>}
        </div>

        <div className="flex items-center gap-4 mt-2">
          {(app.productivity_standard || app.productivity_m2_per_day) && (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 rounded-full">
              <Zap className="w-3 h-3" /> {Number(app.productivity_standard || app.productivity_m2_per_day).toFixed(0)} m²/day
            </span>
          )}
          {app.total_cost_per_m2 && Number(app.total_cost_per_m2) > 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 rounded-full">
              <DollarSign className="w-3 h-3" /> {formatCurrency(Number(app.total_cost_per_m2))}/m²
            </span>
          )}
          {app.warranty_years && (
            <span className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-full">
              <Shield className="w-3 h-3" /> {app.warranty_years}yr warranty
            </span>
          )}
        </div>
      </div>

      <ChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
    </button>
  )
}
